import { useState } from 'react';
import { Form, Input, Button, Checkbox, Modal, App } from 'antd';
import './Login.scss';

// 生成算术人机验证题目
const genCaptcha = () => {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { a, b };
};

// 账号支持「手机号」或「邮箱」两种模式
const isPhone = (v) => /^1[3-9]\d{9}$/.test(v);
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

function Login() {
  const { message } = App.useApp();
  const [mode, setMode] = useState('login');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 隐私协议弹窗
  const [privacyOpen, setPrivacyOpen] = useState(false);
  // 人机验证（注册专用）
  const [captcha, setCaptcha] = useState(genCaptcha);
  // 注册审核中状态
  const [reviewing, setReviewing] = useState(false);

  const refreshCaptcha = () => {
    setCaptcha(genCaptcha());
    form.setFieldsValue({ captcha: '' });
  };

  const switchMode = (m) => {
    setMode(m);
    setReviewing(false);
    form.resetFields();
    if (m === 'register') refreshCaptcha();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // 登录：维护期暂不开放，无论输入如何均提示错误
      if (mode === 'login') {
        message.error('用户名或密码错误');
        return;
      }

      // 注册：人工审核模式，提交后展示审核中，不即时创建账号
      setReviewing(true);
      message.success('申请已提交，正在进行人工审核');
    } catch (err) {
      // 校验错误会被 antd 拦截，这里只处理真正的异常
      if (err && err.errorFields) return;
      message.error('提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="container">
        <div className="login-card">
          <img src="/logo.png" alt="好美意" className="login-logo-img" />

          {reviewing ? (
            <div className="review-box">
              <div className="review-icon">⏳</div>
              <h2>审核进行中</h2>
              <p>
                您的注册申请已提交，正在进行<strong>人工审核</strong>。
                <br />
                审核结果将通过您填写的手机号或邮箱通知您，请耐心等待。
              </p>
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  setReviewing(false);
                  form.resetFields();
                }}
              >
                返回修改
              </Button>
            </div>
          ) : (
            <>
              <h1>好美意 · {mode === 'login' ? '登录' : '注册'}</h1>
              <p className="login-sub">
                {mode === 'login'
                  ? '登录后享受专属顾问与付费咨询服务'
                  : '注册账号，开启您的专属家居尊享服务'}
              </p>

              <div className="login-switch">
                <button
                  type="button"
                  className={mode === 'login' ? 'active' : ''}
                  onClick={() => switchMode('login')}
                >
                  登录
                </button>
                <button
                  type="button"
                  className={mode === 'register' ? 'active' : ''}
                  onClick={() => switchMode('register')}
                >
                  注册
                </button>
              </div>

              <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item
                  name="account"
                  label="手机号 / 邮箱"
                  rules={[
                    { required: true, message: '请输入手机号或邮箱' },
                    {
                      validator(_, value) {
                        if (!value) return Promise.resolve();
                        if (isPhone(value) || isEmail(value)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('请输入正确的手机号或邮箱'));
                      },
                    },
                  ]}
                >
                  <Input size="large" placeholder="请输入手机号或邮箱" autoFocus />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="密码"
                  rules={[
                    { required: true, message: '请输入密码' },
                    { min: 6, message: '密码至少 6 位' },
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="请输入密码"
                    onPressEnter={handleSubmit}
                  />
                </Form.Item>

                {mode === 'register' && (
                  <Form.Item
                    name="confirm"
                    label="确认密码"
                    dependencies={['password']}
                    rules={[
                      { required: true, message: '请再次输入密码' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('两次输入的密码不一致'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      size="large"
                      placeholder="请再次输入密码"
                      onPressEnter={handleSubmit}
                    />
                  </Form.Item>
                )}

                {/* 注册：人机验证（算术题） */}
                {mode === 'register' && (
                  <Form.Item
                    name="captcha"
                    label="人机验证"
                    rules={[
                      { required: true, message: '请输入计算结果' },
                      {
                        validator(_, value) {
                          if (Number(value) === captcha.a + captcha.b) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('验证结果不正确'));
                        },
                      },
                    ]}
                  >
                    <div className="captcha-row">
                      <Input
                        size="large"
                        placeholder={`请输入 ${captcha.a} + ${captcha.b} 的结果`}
                        onPressEnter={handleSubmit}
                      />
                      <button
                        type="button"
                        className="captcha-refresh"
                        onClick={refreshCaptcha}
                      >
                        刷新
                      </button>
                    </div>
                  </Form.Item>
                )}

                {/* 隐私协议（登录 / 注册均需勾选） */}
                <Form.Item
                  name="agree"
                  valuePropName="checked"
                  rules={[
                    {
                      validator(_, value) {
                        if (value) return Promise.resolve();
                        return Promise.reject(new Error('请先阅读并同意《隐私协议》'));
                      },
                    },
                  ]}
                >
                  <Checkbox>
                    我已阅读并同意
                    <Button
                      type="link"
                      className="privacy-link"
                      onClick={() => setPrivacyOpen(true)}
                    >
                      《隐私协议》
                    </Button>
                  </Checkbox>
                </Form.Item>

                <Button
                  type="primary"
                  size="large"
                  block
                  loading={loading}
                  onClick={handleSubmit}
                  className="login-btn"
                >
                  {mode === 'login' ? '登 录' : '提交审核'}
                </Button>
              </Form>

            </>
          )}
        </div>
      </div>

      {/* 隐私协议弹窗 */}
      <Modal
        title="隐私协议"
        open={privacyOpen}
        onOk={() => setPrivacyOpen(false)}
        onCancel={() => setPrivacyOpen(false)}
        okText="我已知晓"
        cancelText="关闭"
      >
        <div className="privacy-content">
          <p>
            欢迎使用「好美意」网站及服务。我们高度重视您的个人信息与隐私保护。在您使用本平台前，请仔细阅读以下条款：
          </p>
          <p>
            <strong>一、信息收集</strong>
            <br />
            为提供账号登录、专属顾问咨询、付费尊享服务等核心功能，我们可能收集您主动填写的手机号或邮箱、密码（加密存储）及服务偏好等信息。
          </p>
          <p>
            <strong>二、信息使用</strong>
            <br />
            您的信息仅用于为您提供服务、优化体验及必要的业务通知，未经您的明示同意，我们不会向无关第三方泄露您的个人信息。
          </p>
          <p>
            <strong>三、信息保护</strong>
            <br />
            我们采用加密传输与访问控制等技术措施保护您的信息安全，并仅在实现服务目的所必需的时间内保留相关数据。
          </p>
          <p>
            <strong>四、您的权利</strong>
            <br />
            您有权查询、更正或删除您的个人信息，并可随时撤回已作出的授权同意。
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default Login;
